import { el, setChildren } from 'redom';

import application from 'application';

import CancelButton from 'generics/components/cancel-button';
import SaveButton from 'generics/components/save-button';

import internalEventCodes from 'event-system/codes/internal';

import {
  applyTraits
} from 'utilities/javascript';

import ModifiedSectionsTracker from 'root/startup-view/calendar/activity-editor/editors/modified-sections-tracker';
import BasicDetailsSection from 'root/startup-view/calendar/activity-editor/editors/sections/basic-details';
import ScheduleSection from 'root/startup-view/calendar/activity-editor/editors/sections/schedule';
import ParticipantsSection from 'root/startup-view/calendar/activity-editor/editors/sections/participants';
import LocationSection from 'root/startup-view/calendar/activity-editor/editors/sections/location';
import RemindersSection from 'root/startup-view/calendar/activity-editor/editors/sections/reminders';

import {
  convertAttendeeIdsToAttendees
} from 'root/startup-view/calendar/activity-editor/editors/meeting/utilities';

import 'root/startup-view/calendar/activity-editor/editors/meeting/index.scss';

class PublicMeetingEditor {

  constructor(configuration) {

    this.meeting = configuration.meeting;
    this.onSave = configuration.onSave;
    this.onCancel = configuration.onCancel;
    this.mode = configuration.mode;
    this.readOnly = configuration.readOnly;
    this.hideCancelButton = configuration.hideCancelButton || false;
    this.modifiedSections = [];
    this.onRemindersChange = configuration.onRemindersChange || function noop() {};

    this.cancelButton = new CancelButton({ label: 'Cancel Appointment', onClick: this.handleCancelButtonClick.bind(this) });
    this.saveButton = new SaveButton({
      label: 'Reschedule',
      onClick: this.saveMeeting.bind(this),
      disabled: this.modifiedSections.length === 0
    });
    this.basicDetailsSection = null;
    this.scheduleSection = null;
    this.attendeesSection = null;
    this.locationSection = null;
    this.remindersSection = null;
    this.sections = [];
    this.initializeSections();

    this.controlsSection = el('#meeting-activity-editor-panel-controls-section', this.determineControlButtons());

    this.el = el('#meeting-activity-editor-panel-main', [
      el('#meeting-activity-editor-panel-inputs-section', {
        class: 'hide-scrollbar'
      }, [
        this.basicDetailsSection,
        this.scheduleSection,
        this.attendeesSection,
        this.locationSection,
        this.remindersSection
      ]),
      this.controlsSection
    ]);

  }

  determineControlButtons() {

    const buttons = [];

    if (this.cancelled === true) {

      this.saveButton.setDisabled(true);
      this.saveButton.update({ label: 'Meeting Cancelled' });

      buttons.push(this.saveButton);

      return buttons;

    }

    if (this.hideCancelButton === false) {
      buttons.push(this.cancelButton);
    }

    buttons.push(this.saveButton);

    return buttons;

  }

  setErrors(errors) {

    const sections = this.sections;

    for (let section of sections) {
      section.setErrors(errors);
    }

  }

  clearErrors() {

    const sections = this.sections;

    for (let section of sections) {
      section.clearErrors();
    }

  }

  async handleCancelButtonClick() {

    const confirmed = await application.prompts.confirm({
      header: 'Cancel this appointment?',
      description: 'This cancels the appointment for everyone and notifies all attendees. This cannot be undone.',
      confirmButtonText: 'Yes, cancel it',
      cancelButtonText: 'No'
    });

    if (confirmed !== true) {
      return;
    }

    this.cancelled = true;

    setChildren(this.controlsSection, this.determineControlButtons());

    this.onCancel();

  }

  initializeSections() {

    this.basicDetailsSection = new BasicDetailsSection({
      event: this.meeting,
      readOnly: this.readOnly,
      onChange: this.handleBasicDetailsChange.bind(this)
    });
    this.scheduleSection = new ScheduleSection({
      event: this.meeting,
      readOnly: false,
      skipRecurring: true,
      enforceSameDate: true,
      onChange: this.handleScheduleChange.bind(this)
    });
    this.attendeesSection = new ParticipantsSection({
      placeholder: 'People',
      participants: this.meeting.attendees,
      readOnly: this.readOnly,
      onChange: this.handleParticipantsChange.bind(this),
      contactRenderer: 'meeting-attendee'
    });
    this.locationSection = new LocationSection({
      event: this.meeting,
      readOnly: this.readOnly,
      onChange: this.handleLocationChange.bind(this)
    });

    this.remindersSection = new RemindersSection({
      event: this.meeting,
      onChange: this.handleRemindersChange.bind(this)
    });

    this.sections.push(this.basicDetailsSection, this.scheduleSection, this.attendeesSection, this.locationSection, this.remindersSection);

  }

  buildMeetingData() {

    const basicDetails = this.basicDetailsSection.getValue();
    const schedule = this.scheduleSection.getValue();
    const assigneeIds = this.attendeesSection.getValue();
    const reminders = this.remindersSection.getValue();

    const data = {
      ...basicDetails,
      ...schedule,
      attendees: convertAttendeeIdsToAttendees(assigneeIds),
      reminders
    };

    return data;

  }

  async saveMeeting() {

    const data = this.buildMeetingData();

    this.clearErrors();

    try {

      await this.onSave(data);

      this.resetModifiedStates();

    } catch (exception) {

      if (exception.code !== 'ValidationError') {
        // eslint-disable-next-line no-console
        console.error(exception);
        application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
          message: 'Unknown error! Please contact administration.',
          seconds: 5,
          type: 'error'
        });
        return;
      }

      this.setErrors(exception.metadata);

    }

  }

}

PublicMeetingEditor.modes = {
  CREATE: 'create',
  EDIT: 'edit'
};

export default applyTraits(PublicMeetingEditor, [ModifiedSectionsTracker]);